
import React, { useState, useEffect, useCallback } from 'react';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error';
}

let toastId = 0;
let listeners: Array<(toasts: ToastMessage[]) => void> = [];
let toasts: ToastMessage[] = [];

const toast = (message: string, type: 'success' | 'error' = 'success') => {
  toasts = [...toasts, { id: toastId++, message, type }];
  listeners.forEach((listener) => listener(toasts));
};

export const useToaster = () => {
  const [state, setState] = useState(toasts);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      listeners = listeners.filter((l) => l !== setState);
    };
  }, [state]);

  const removeToast = useCallback((id: number) => {
    toasts = toasts.filter((t) => t.id !== id);
    listeners.forEach((listener) => listener(toasts));
  }, []);

  return { toasts: state, removeToast, toast };
};

const Toast: React.FC<{ toast: ToastMessage; onDismiss: (id: number) => void }> = ({ toast, onDismiss }) => {
    const [isExiting, setIsExiting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsExiting(true);
            const dismissTimer = setTimeout(() => onDismiss(toast.id), 300);
            return () => clearTimeout(dismissTimer);
        }, 4000);

        return () => clearTimeout(timer);
    }, [toast.id, onDismiss]);

    const handleDismiss = () => {
        setIsExiting(true);
        setTimeout(() => onDismiss(toast.id), 300);
    };
    
    const bgClass = toast.type === 'success' ? 'bg-green-500/90' : 'bg-red-500/90';

    return (
        <div 
            className={`flex items-center w-full max-w-xs p-4 space-x-4 text-white ${bgClass} rounded-lg shadow-lg backdrop-blur-sm ${isExiting ? 'animate-fade-out' : 'animate-slide-in'}`}
            role="alert"
        >
            <div className="text-sm font-normal">{toast.message}</div>
            <button
                type="button"
                className="-mx-1.5 -my-1.5 ml-auto inline-flex h-8 w-8 rounded-lg p-1.5 hover:bg-white/20 focus:ring-2 focus:ring-gray-300"
                onClick={handleDismiss}
                aria-label="Close"
            >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
            </button>
        </div>
    );
};

export const Toaster: React.FC = () => {
    const { toasts, removeToast } = useToaster();

    return (
        <div className="fixed bottom-5 right-5 z-50 space-y-2">
            {toasts.map((t) => (
                <Toast key={t.id} toast={t} onDismiss={removeToast} />
            ))}
        </div>
    );
};

// Export the toast function to be used anywhere in the app
export { toast };
